import type { GrantPreview, TimeEntry } from "@tovi/shared";
import { api, baseUrl, token } from "./api.ts";

/**
 * Who is at the keys (`02-architecture.md` § Auth).
 *
 * The terminal's bearer token says which device this is and is set up once.
 * This says which *person* is on it, changes when they hand over, and is what
 * a shift is attributed to. A drawer counted by "BACARO terminal 1" answers
 * nothing; a drawer counted by Sophea does.
 *
 * Kept in `sessionStorage` rather than `localStorage`, deliberately. The
 * terminal's token is a fact about the device and survives a restart; a
 * cashier is a fact about right now, and a till reopened the next morning
 * should ask again rather than assume last night's person is back.
 *
 * **The cashier is the authorisation.** The terminal's token proves the device
 * and establishes the store; the grant proves the person; and the engine
 * resolves the actor from the assertion, so `requirePermission` reads *this*
 * person's keys. Every call the till makes carries `X-Staff-Session`, not only
 * the shift routes.
 *
 * `permissions` below is what the server said that person's role grants, and it
 * is here so the screen can be **honest** rather than so it can enforce: a
 * button for something that will be refused is worse than no button, and a
 * refusal that arrives anyway — a role edited mid-shift, a queue drained after
 * the change — is still the server's answer and still has to be rendered. The
 * two other fields stay what they were: `canOperateRegister` is a column on the
 * person and not a permission, and `roleName` is a label.
 */

const KEY = "tovi.pos.staff";

export type Cashier = {
  readonly token: string;
  readonly expiresAt: string;
  readonly id: string;
  readonly name: string;
  readonly roleName: string | null;
  readonly canOperateRegister: boolean;
  /** Null means no photo; otherwise the cache-busting `v` for their photo. */
  readonly photoVersion: string | null;
  /** What the server says their role grants. What to render, never what to enforce. */
  readonly permissions: readonly string[];
};

type Listener = () => void;
const listeners = new Set<Listener>();
let snapshot: Cashier | null = read();

function read(): Cashier | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const held = JSON.parse(raw) as Cashier;
    // An assertion that has run out is not a cashier. Checked on read rather
    // than by a timer, because the only moment it matters is the next request.
    if (new Date(held.expiresAt).getTime() <= Date.now()) return null;
    return held;
  } catch {
    // Storage a browser refused, or a shape from an older build. Either way
    // there is nobody signed in, which is the safe reading.
    return null;
  }
}

function write(next: Cashier | null): void {
  snapshot = next;
  try {
    if (next) sessionStorage.setItem(KEY, JSON.stringify(next));
    else sessionStorage.removeItem(KEY);
  } catch {
    // A terminal with storage blocked still works for this session; it just
    // asks whoever is at it to sign in again after a reload.
  }
  for (const listener of listeners) listener();
}

export function subscribeToCashier(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `useSyncExternalStore` compares by identity, so this must not rebuild. */
export function currentCashier(): Cashier | null {
  return snapshot;
}

/** The body shape `/grant` answers with. */
type StaffSessionAnswer = {
  token: string;
  expiresAt: string;
  employee: {
    id: string;
    name: string;
    roleName: string | null;
    canOperateRegister: boolean;
    photoVersion: string | null;
  };
  permissions: string[];
};

/** Maps a session answer to a `Cashier` and remembers it, for whichever route asked. */
function installCashier(answer: StaffSessionAnswer): Cashier {
  const cashier: Cashier = {
    token: answer.token,
    expiresAt: answer.expiresAt,
    id: answer.employee.id,
    name: answer.employee.name,
    roleName: answer.employee.roleName,
    canOperateRegister: answer.employee.canOperateRegister,
    photoVersion: answer.employee.photoVersion,
    permissions: answer.permissions,
  };
  write(cashier);
  return cashier;
}

/**
 * Trades a redeemed identification grant for a staff assertion (`#212`,
 * `apps/pos/src/lib/identify.ts`).
 *
 * The grant travels in the body, never as a bearer: `api()` still carries the
 * terminal's own token in `Authorization`, which is what
 * `requireTerminalSession` needs, and issue 207's middleware refuses the
 * grant outright if it ever rode there instead.
 */
export async function signInWithGrant(accessToken: string): Promise<Cashier> {
  const answer = await api<StaffSessionAnswer>("/store/shifts/grant", {
    method: "POST",
    body: { accessToken },
  });
  return installCashier(answer);
}

/**
 * What the same grant would let a till offer, without spending it (`#219`,
 * `#229`) — the choice screen's data, read as many times as it needs before
 * it commits to a punch.
 */
export async function previewGrant(accessToken: string): Promise<GrantPreview> {
  return api<GrantPreview>("/store/shifts/grant/preview", {
    method: "POST",
    body: { accessToken },
  });
}

/**
 * Spends the grant on a punch, optionally taking the till in the same call
 * (`#219`, `#229`). A refusal leaves the grant unspent, so the till may retry
 * with the other action.
 *
 * `cashier` is installed through the same `installCashier` helper
 * `signInWithGrant` uses, and is non-null only when `takeTill` was asked for
 * — checking out never returns a session.
 */
export async function clockByGrant(input: {
  accessToken: string;
  action: "in" | "out";
  jobId?: string;
  takeTill: boolean;
}): Promise<{ entry: TimeEntry; cashier: Cashier | null }> {
  const answer = await api<{ entry: TimeEntry; session: StaffSessionAnswer | null }>(
    "/store/shifts/grant/clock",
    { method: "POST", body: input },
  );
  return { entry: answer.entry, cashier: answer.session ? installCashier(answer.session) : null };
}

/**
 * Object URLs for employee faces — the identify screen's panels, the shift
 * panel and the header chip.
 *
 * `<img src>` cannot carry a bearer header, and the till's photo route needs
 * one — it is a cross-origin client with no cookie, and
 * `secureHeaders()` also defaults `Cross-Origin-Resource-Policy: same-origin`,
 * which blocks a cross-origin `<img>` load outright regardless. So this
 * fetches the bytes itself and holds an object URL.
 *
 * Memory only, keyed by employee id and photo version, and evicted rather
 * than merely overwritten: keying on the version means a changed photo
 * replaces itself and a stale one is never shown, and revoking the old URL is
 * what actually frees it. A till that cannot reach the API cannot sign anyone
 * in either — a persistent cache would serve a case that cannot happen.
 */
const photoCache = new Map<string, string>();
const photoLoads = new Map<string, Promise<string | null>>();

function evictPhotosFor(employeeId: string, keep: string): void {
  for (const [key, url] of photoCache) {
    if (key !== keep && key.startsWith(`${employeeId}:`)) {
      URL.revokeObjectURL(url);
      photoCache.delete(key);
    }
  }
}

/** The object URL already held for this face, or null — never fetches. */
export function cachedPhoto(employeeId: string, photoVersion: string): string | null {
  return photoCache.get(`${employeeId}:${photoVersion}`) ?? null;
}

/** Fetches and memoises a face, or resolves null if there is none to show. */
export async function loadPhoto(employeeId: string, photoVersion: string): Promise<string | null> {
  const key = `${employeeId}:${photoVersion}`;
  const cached = photoCache.get(key);
  if (cached) return cached;

  const inflight = photoLoads.get(key);
  if (inflight) return inflight;

  const promise = (async () => {
    const held = token();
    const response = await fetch(
      `${baseUrl()}/api/store/staff/${employeeId}/photo?v=${encodeURIComponent(photoVersion)}`,
      { headers: held ? { Authorization: `Bearer ${held}` } : {} },
    );
    if (!response.ok) return null;
    const url = URL.createObjectURL(await response.blob());
    evictPhotosFor(employeeId, key);
    photoCache.set(key, url);
    return url;
  })().finally(() => photoLoads.delete(key));

  photoLoads.set(key, promise);
  return promise;
}

/**
 * Whether the cashier's role grants a key.
 *
 * Gated on the permission the server will actually check, never on a role code:
 * a merchant may edit the seeded roles (`07-entitlements-rbac.md` § 3), so
 * `roleName === "Cashier"` is a guess about a name rather than a rule about
 * rights.
 *
 * False with nobody signed in, which is the honest answer — a till before its
 * first sign-in of the day should offer nothing that needs a person.
 */
export function can(key: string): boolean {
  return currentCashier()?.permissions.includes(key) ?? false;
}

/** Handing over. The terminal stays signed in; the person does not. */
export function signOutCashier(): void {
  write(null);
}

/** The assertion this till is holding, or null. */
export function staffToken(): string | null {
  return currentCashier()?.token ?? null;
}

/**
 * The header **every** till call carries, or nothing.
 *
 * It used to ride only on the two shift routes, because it said who and
 * nothing more. It is now what authorises the request, so a call that omits it
 * is a call authorised as the terminal — which is the whole defect this
 * replaced. Nothing rather than an error is still right for the one case that
 * remains: a till that nobody has signed in to yet, where the terminal's own
 * authority is all there is and the sign-in screen is what comes next.
 */
export function staffHeaders(): Record<string, string> {
  const held = staffToken();
  return held ? { "X-Staff-Session": held } : {};
}
